/**
 * Reduced motion for the home hero carousel — BUILD-PLAN.md §8.1a.
 *
 * **This exists because Horizon's slideshow does not honour
 * `prefers-reduced-motion`, despite §8.1a assuming it did.** Measured 28 Aug
 * 2026 against `assets/slideshow.js`: the file contains no `matchMedia`, never
 * imports `prefersReducedMotion` from `utilities.js` — which Horizon does
 * export, and does use elsewhere — and `#setupSlideshow()` calls `resume()`
 * unconditionally, which starts the timer. The component's only reduced-motion
 * rule is `scroll-behavior: auto` in `snippets/slideshow-styles.liquid`, and
 * that makes the *transition* instant while leaving the slides advancing. For
 * someone who asked for less motion that is arguably the worse outcome: the
 * content still changes under them, now with no movement to explain why.
 *
 * So the timer is stopped here instead. `pause()` is Horizon's own public
 * method — it clears the interval and sets the `paused` attribute — so this
 * neither subclasses nor rewrites the component, per the CLAUDE.md rule about
 * extending Horizon's web components rather than editing them.
 *
 * **It reacts to the setting changing, not just its value at load.** macOS and
 * iOS expose Reduce Motion as a toggle a user flips while a page is open, and
 * a carousel that only checked once would keep spinning until a reload.
 *
 * **It also relabels the pager, which is a second Horizon bug.** The dots are
 * `<button aria-selected="…">`, and `aria-selected` is not an allowed attribute
 * on a button — it belongs to `option`, `tab`, `row` and friends. axe scores
 * that `critical`, and §8.1a's gate is "no new baseline entries", so it is
 * fixed rather than waved through. `snippets/slideshow-controls.liquid` emits it
 * for both the `dots` and `thumbnails` styles, and `slideshow.js:428` rewrites
 * it on every slide change, so removing it is not an option: it would be back
 * on the next tick, and Horizon's own `[aria-selected='true']` CSS — which
 * carries the active dot's progress fill — would stop matching.
 *
 * Adding the roles that *permit* the attribute fixes it from the other side.
 * A dot pager is the ARIA Authoring Practices "carousel with tabbed slide
 * picker", so `tablist` / `tab` is the honest description rather than a trick
 * to quiet the checker, and because roles never change it is applied once
 * instead of fought for on every advance. `aria-controls` is deliberately not
 * wired: it would mean minting ids for the slides and marking them
 * `tabpanel`, and measured against axe the roles alone already clear the
 * violation with nothing new appearing.
 *
 * The `aria-live` removal is belt and braces. `sections/sd-hero.liquid` already
 * avoids it by setting the `autoplay` attribute through the snippet's
 * `attributes` parameter rather than its `autoplay` one — see the note there —
 * but that dodge depends on an upstream `{% if %}` staying as it is, and a live
 * region on an autoplaying carousel is the failure §8.1a explicitly refuses to
 * inherit. Cheap to assert here, and it means an upgrade that changes the
 * snippet degrades quietly rather than loudly.
 */

const QUERY = '(prefers-reduced-motion: reduce)';

/** @param {Element} component */
function applyMotionPreference(component, reduced) {
  // Never announce an autoplaying carousel, whatever the Liquid produced.
  component.removeAttribute('aria-live');

  if (reduced) {
    // Remember the speed so the preference can be reversed without a reload.
    const speed = component.getAttribute('autoplay');
    if (speed) component.dataset.sdAutoplay = speed;
    component.removeAttribute('autoplay');
    if (typeof component.pause === 'function') component.pause();
    return;
  }

  if (!component.hasAttribute('autoplay') && component.dataset.sdAutoplay) {
    component.setAttribute('autoplay', component.dataset.sdAutoplay);
    if (typeof component.play === 'function') {
      component.paused = false;
      component.play();
    }
  }
}

/**
 * Make the dot pager a tablist, so its `aria-selected` is legal ARIA.
 * @param {Element} component
 */
function relabelPager(component) {
  const list = component.querySelector('.slideshow-controls__dots');
  if (!list || list.getAttribute('role') === 'tablist') return;

  list.setAttribute('role', 'tablist');
  // The <li>s are presentational: a `tab` has to find its `tablist` through
  // them, and a list item in between would break that ancestry.
  list.querySelectorAll(':scope > li').forEach((item) => item.setAttribute('role', 'presentation'));
  list.querySelectorAll('button').forEach((dot) => dot.setAttribute('role', 'tab'));
}

async function init() {
  // Wait for the upgrade, or `pause` and `play` are not on the element yet and
  // removing the attribute would be undone by the component's own setup.
  await customElements.whenDefined('slideshow-component');

  const components = document.querySelectorAll('.sd-hero slideshow-component');
  if (components.length === 0) return;

  components.forEach(relabelPager);

  const media = matchMedia(QUERY);
  const sync = () => components.forEach((component) => applyMotionPreference(component, media.matches));

  sync();
  media.addEventListener('change', sync);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
